import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {API_URLS, SECURE_API_URLS} from '../config/api.urls';
import { isPlatformBrowser } from '@angular/common';

import { AuthenticationService } from './authentication.service';
import { Router} from '@angular/router';
import { ResetPassword } from '../_models/reset-password';
import { BehaviorSubject, Observable } from 'rxjs';
import { ShippingAddress } from '../_models/shipping-address';
import {User} from '../_models/user';
import { AppConstants } from '../_constants/constants';
import {IPagination} from '../_models/pagination';

@Injectable()
export class UserService {
    private currentUserSubject: BehaviorSubject<User | null>;
    public currentUser$: Observable<User | null>;

    constructor(private http: HttpClient, private authService: AuthenticationService, private router: Router, @Inject(PLATFORM_ID) private platformId: Object) {
        const storedUser = isPlatformBrowser(this.platformId)
            ? localStorage.getItem(AppConstants.CURRENT_USER_KEY)
            : null;
        const initialUser = storedUser ? JSON.parse(storedUser) : null;
        this.currentUserSubject = new BehaviorSubject<User | null>(initialUser);
        this.currentUser$ = this.currentUserSubject.asObservable();
    }

    register(user: User) {
        return this.http.post(API_URLS.REGISTER, user);
    }

    load(): Observable<User> {
        return this.http.get<User>(API_URLS.LOAD_CURRENT_USER_DETAILS);
    }

    createPrivateSellerAccount(userId: number, data: any): Observable<any> {
        return this.http.post(API_URLS.CREATE_PRIVATE_SELLER_ACCOUNT + '/' + userId + '/createPrivateSellerAccount', data);
    }

    createDeliveryAgent(userId: number, data: any): Observable<any> {
        return this.http.post(API_URLS.CREATE_DELIVERY_AGENT + '/' + userId + '/createLogisticAgent', data);
    }

    loadByAdmin(userId: number): Observable<any> {
        return this.http.get(API_URLS.LIST_OF_USERS + '/' + userId + '/load');
    }

    loadProfilePicture(userId: number): Observable<any> {
        return this.http.get(API_URLS.LOAD_CURRENT_USER_DETAILS +  '/' + userId + '/load-picture');
    }

    reset_password( email_or_phone: string ) {
        return this.http.post(API_URLS.FORGET_PASSWORD, {emailOrPhone: email_or_phone});
    }

    isAdmin(): boolean {
        // Check if we're in browser environment
        if (!isPlatformBrowser(this.platformId)) {
            return false;
        }
        const currentUserJson = localStorage.getItem('currentUser');
        const currentUser = currentUserJson ? JSON.parse(currentUserJson) : null;
        return !!(currentUser && currentUser.role && currentUser.role.toLowerCase() === 'admin');
    }

    isLoggedIn (): boolean {
        // Check if we're in browser environment
        if (!isPlatformBrowser(this.platformId)) {
            return false;
        }
        const currentUserJson = localStorage.getItem('currentUser');
        const currentUser = currentUserJson ? JSON.parse(currentUserJson) : null;
        return !!(currentUser && currentUser.role && (currentUser.role.toLowerCase() === 'admin' || currentUser.role.toLowerCase() === 'user'
            || currentUser.role.toLowerCase() === 'business' || currentUser.role.toLowerCase() === 'buyer' || currentUser.role.toLowerCase() === 'seller' || currentUser.role.toLowerCase() === 'carrier'));
    }

    verify_sign_up_token(token: string) {
        return this.http.get(API_URLS.VERIFY_SIGN_UP_TOKEN + '/' + token);
    }

    verify_password_reset_token(token: string) {
        return this.http.post(API_URLS.PASSWORD_VERIFY_TOKEN, { token: token });
    }

    resetPassword(rs: ResetPassword) {
        return this.http.post(API_URLS.UPDATE_PASSWORD, rs);
    }

    logout () {
        this.authService.logout();
        this.setCurrentUser(null);
        this.router.navigateByUrl('/signin');
    }

    loadUsers(page: number): Observable<any> {
        return this.http.get(API_URLS.LIST_OF_USERS, { params: { page: page }});
    }

    searchUsers(values: any, page: number): Observable<any> {
        return this.http.get(API_URLS.LIST_OF_USERS + '/' +'search', { params:
                {
                    searchTerm: (values.searchTerm == null ? '' : values.searchTerm),
                    role: (values.role == null ? '' : values.role),
                    page: page
                }
        });
    }

    loadShippingAddresses(user_id: number): Observable<ShippingAddress[]> {
        return this.http.get<ShippingAddress[]>(API_URLS.LOAD_CURRENT_USER_DETAILS + '/' + user_id + '/shipping-addresses');
    }

    saveShippingAddress(user_id: number, shippingAddress: ShippingAddress): Observable<any> {
        if ( shippingAddress.id > 0 ) {
            return this.http.put(API_URLS.LOAD_CURRENT_USER_DETAILS + '/' + user_id + '/shipping-addresses', shippingAddress);
        } else {
            return this.http.post(API_URLS.LOAD_CURRENT_USER_DETAILS + '/' + user_id + '/shipping-addresses', shippingAddress);
        }
    }

    updateShippingAddress(addressId: number, payload: any): Observable<any> {
        return this.http.put(API_URLS.LOAD_CURRENT_USER_DETAILS + '/shipping-addresses/' + addressId, payload);
    }

    deleteShippingAddress(shippingAddress: ShippingAddress) {
        return this.http.delete(API_URLS.LOAD_CURRENT_USER_DETAILS + '/shipping-addresses/' + shippingAddress.id);
    }

    addUser(user: User) {
        return this.http.post(API_URLS.LIST_OF_USERS + '/' + 'add', user);
    }

    updateUser(user: User) {
        return this.http.put(API_URLS.LOAD_CURRENT_USER_DETAILS + '/' + user.id , user);
    }

    updateUserByAdmin(userId: number, payload: any): Observable<any> {
        return this.http.put(API_URLS.LIST_OF_USERS + '/' + userId, payload);
    }

    changeUserPassword(user: User, formValues: any) {
        const data = new FormData();
        data.append('oldPassword', formValues.oldPassword);
        data.append('newPassword', formValues.newPassword);
        return this.http.put(API_URLS.LOAD_CURRENT_USER_DETAILS + '/' + user.id + '/update-password', data);
    }

    changeStatus(user: User, status: number): Observable<any> {
        return this.http.get(API_URLS.LIST_OF_USERS + '/' + user.id + '/change_status',  { params: {status: status}});
    }

    uploadPicture(user: User, file: Blob): Observable<any> {
        const  fb = new FormData();
        fb.append('picture', file);
        return this.http.post(API_URLS.LOAD_CURRENT_USER_DETAILS + '/' + user.id + '/upload', fb );
    }

    deleteUser(user: User): Observable<any> {
        return this.http.delete(API_URLS.LIST_OF_USERS + '/' + user.id);
    }

    getCurrntUser(): User | null {
        return this.currentUserSubject.getValue();
    }

    setCurrentUser(user: User | null): void {
        if (isPlatformBrowser(this.platformId)) {
            if (user) {
                localStorage.setItem(AppConstants.CURRENT_USER_KEY, JSON.stringify(user));
            } else {
                localStorage.removeItem(AppConstants.CURRENT_USER_KEY);
            }
        }
        this.currentUserSubject.next(user);
    }

    searchEFarms(values: any, page: any): Observable<IPagination> {
        return this.http.get<IPagination>(SECURE_API_URLS.USERS_SEARCH_BASE_URL, { params:
                {
                    searchTerm: values.searchTerm,
                    sectorId: values.sectorId,
                    countryId: values.country,
                    page: page
                }
        });
    }

    getSellerAccount(userId: number): Observable<any> {
        return this.http.get(API_URLS.GET_SELLER_ACCOUNT + '/' + userId + '/sellerAccount');
    }

    getCarrierDetails(userId: number): Observable<any> {
        const url = API_URLS.GET_CARRIER_DETAILS + '/' + userId + '/logisticPartner';
        console.log('=== API CALL: getCarrierDetails ===');
        console.log('URL:', url);
        return this.http.get(url);
    }

    createLogisticCompany(userId: number, data: any): Observable<any> {
        return this.http.post(API_URLS.CREATE_LOGISTIC_COMPANY + '/' + userId + '/createLogisticCompany', data);
    }

    requestPhoneVerificationCode(userId: number, payload: any): Observable<any> {
        return this.http.post(API_URLS.REQUEST_PHONE_VERIFICATION_CODE + '/' + userId + '/requestPhoneVerificationCode', payload);
    }

    validateVerificationCode(userId: number, payload: any): Observable<any> {
        return this.http.post(API_URLS.VALIDATE_VERIFICATION_CODE + '/' + userId + '/validateVerificationCode', payload);
    }

    updatePrivateSellerAccount(userId: number, data: any): Observable<any> {
        return this.http.put(API_URLS.UPDATE_PRIVATE_SELLER_ACCOUNT + '/' + userId + '/updatePrivateSellerAccount', data);
    }

    createCompanySellerAccount(userId: number, data: any): Observable<any> {
        return this.http.post(API_URLS.CREATE_COMPANY_SELLER_ACCOUNT + '/' + userId + '/createCompanySellerAccount', data);
    }

    updateCompanySellerAccount(userId: number, data: any): Observable<any> {
        return this.http.put(API_URLS.UPDATE_COMPANY_SELLER_ACCOUNT + '/' + userId + '/updateCompanySellerAccount', data);
    }

    completeSellerAccount(userId: number, data: any): Observable<any> {
        return this.http.post(API_URLS.COMPLETE_SELLER_ACCOUNT + '/' + userId + '/accountComplete?partnerType=seller', data);
    }

    completeCarrierAccount(userId: number, data: any): Observable<any> {
        return this.http.post(API_URLS.COMPLETE_CARRIER_ACCOUNT + '/' + userId + '/accountComplete?partnerType=carrier', data);
    }

    updateLogisticAgent(userId: number, data: any): Observable<any> {
        return this.http.put(API_URLS.UPDATE_LOGISTIC_AGENT + '/' + userId + '/updateLogisticAgent', data);
    }

    updateLogisticCompany(userId: number, data: any): Observable<any> {
        return this.http.put(API_URLS.UPDATE_LOGISTIC_COMPANY + '/' + userId + '/updateLogisticCompany', data);
    }

    getPackages(): Observable<any> {
        return this.http.get(API_URLS.LIST_OF_PACKAGES);
    }

    getUserSubscription(): Observable<any> {
        return this.http.get(API_URLS.GET_USER_SUBSCRIPTION);
    }

    getSubscriptions(): Observable<any> {
        return this.http.get(API_URLS.GET_SUBSCRIPTIONS);
    }

    deleteAccount(): Observable<any> {
        return this.http.delete(API_URLS.DELETE_ACCOUNT);
    }

    resendLink(userId: number): Observable<any> {
        return this.http.get(`${API_URLS.RESEND_LINK}/${userId}/resendLink`);
    }
}
